import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EmpreenderApps } from "../../files/AppsArray";
import { appsDaCentral } from "../../lib/appsDaCentral";
import "./metricas.css";
import AdminUsuarios from "./AdminUsuarios";
import TrocarSenha from "./TrocarSenha";
import AvisarSlack from "./AvisarSlack";
import AdminResponsaveis from "./AdminResponsaveis";
import AdminIntegracoes from "./AdminIntegracoes";
import AdminLps from "./AdminLps";
import AdminBase from "./AdminBase";
import { Aviso, chamarSlack, EstadoSlack } from "./slackPainel";

// Painel interno de métricas da central (/admin/metricas). Login pelo Slack (api/slack-login.ts) ou com e-mail e senha (usuários
// cadastrados com npm run admin:usuario); a sessão fica num cookie que esta página não lê
// (HttpOnly), aberto e fechado pela api/admin.ts. Os números vêm de api/metricas.ts.

interface Totais {
  perguntas: number;
  conversas: number;
  resolveu: number;
  nao_resolveu: number;
  atendente: number;
  sem_artigo: number;
  plano_b: number;
  tempo_medio_ms: number | null;
}

interface Dados {
  periodo: { inicio: string; fim: string };
  totais: Totais;
  porDia: { dia: string; perguntas: number; resolveu: number; nao_resolveu: number }[];
  porApp: { app: string; perguntas: number; sem_artigo: number; resolveu: number; nao_resolveu: number; atendente: number }[];
  porOrigem: { canal: string; painel: string; perguntas: number }[];
  topPerguntas: {
    id: number;
    app: string;
    vezes: number;
    exemplo: string;
    sem_artigo: number;
    usou_material?: number;
    resposta: string | null;
    artigo_titulo: string | null;
    artigo_link: string | null;
    resolveu: number;
    nao_resolveu: number;
    aviso: Aviso | null;
  }[];
  naoResolvidas: {
    id: number;
    quando: string;
    app: string;
    canal: string;
    pergunta: string;
    resposta: string | null;
    artigo_titulo: string | null;
    artigo_link: string | null;
    usou_material?: boolean;
    motivo: string | null;
    foi_atendente: boolean;
    depois: string[];
    resumo_atendente: string | null;
    aviso: Aviso | null;
  }[];
  lacunas: { id: number; app: string; vezes: number; exemplo: string; ultima: string; aviso: Aviso | null }[];
  artigos: { titulo: string; link: string; app: string; vezes: number; resolveu: number; nao_resolveu: number }[];
  motivos: { tipo: string; detalhe: string; vezes: number }[];
}

const NOME_MOTIVO: Record<string, string> = {
  nao_entendi: "Não entendi a explicação",
  mais_detalhes: "Não resolveu, vou dar mais detalhes",
  duvida_outra: "Minha dúvida é outra",
};

const NOME_ORIGEM_ATENDENTE: Record<string, string> = {
  rodape: "Link do rodapé",
  cartao: "Cartão \"O que faltou?\"",
  detalhar: "Etapa \"vou dar mais detalhes\"",
  insistencia: "Depois de 2 respostas sem resolver",
  sugerido: "Sugerido pela IA",
};

// Só https vira link: um "javascript:" vindo de fora (link de artigo) viraria script nesta tela.
const linkSeguro = (link: string | null | undefined) => (link && /^https:\/\//i.test(link) ? link : null);

// As respostas da IA vêm em markdown; no painel basta o texto (sem ** e com o texto dos links).
const semMarkdown = (texto: string) =>
  texto
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/(\*\*|__)(.+?)\1/g, "$2")
    .replace(/^#{1,6}\s+/gm, "");

const Artigo: React.FC<{ titulo: string | null; link: string | null }> = ({ titulo, link }) =>
  titulo ? (
    <small>
      Baseado no artigo:{" "}
      {linkSeguro(link) ? (
        <a href={linkSeguro(link)!} target="_blank" rel="noopener noreferrer">
          {titulo}
        </a>
      ) : (
        titulo
      )}
    </small>
  ) : null;

// Linha da janela "Ver conversa completa" (api/metricas.ts, acao "conversa").
interface LinhaConversa {
  quando: string;
  tipo: "cliente" | "ia" | "evento";
  texto: string;
  detalhe?: string | null;
  artigo?: string | null;
  link?: string | null;
}

const descreverEvento = (tipo: string, detalhe?: string | null) => {
  if (tipo === "resolveu") return "Marcou: resolveu";
  if (tipo === "nao_resolveu") return "Marcou: não resolveu";
  if (tipo === "motivo") return `Escolheu: ${NOME_MOTIVO[detalhe || ""] || detalhe || "—"}`;
  if (tipo === "atendente") return `Pediu atendente (${NOME_ORIGEM_ATENDENTE[detalhe || ""] || detalhe || "—"})`;
  if (tipo === "whatsapp") return "Abriu o WhatsApp do atendente";
  return tipo;
};

const JanelaConversa: React.FC<{ linhas: LinhaConversa[] | null; aoFechar: () => void }> = ({ linhas, aoFechar }) => {
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => e.key === "Escape" && aoFechar();
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [aoFechar]);

  return (
    <div className="metricas-janela-fundo" onClick={aoFechar}>
      <div className="metricas-janela" role="dialog" aria-modal="true" aria-label="Conversa completa" onClick={(e) => e.stopPropagation()}>
        <header>
          <h2>Conversa completa</h2>
          <button type="button" className="metricas-janela-fechar" onClick={aoFechar} aria-label="Fechar">
            ×
          </button>
        </header>
        {linhas === null ? (
          <p className="metricas-vazio">Carregando…</p>
        ) : linhas.length ? (
          <ol className="metricas-conversa">
            {linhas.map((l, i) => (
              <li key={i} className={`metricas-msg ${l.tipo}`}>
                {l.tipo === "evento" ? (
                  <>
                    <span>
                      {descreverEvento(l.texto, l.tipo === "evento" && l.texto !== "whatsapp" ? l.detalhe : null)}
                      <small>{l.quando}</small>
                    </span>
                    {l.texto === "whatsapp" && l.detalhe && <p>{l.detalhe}</p>}
                  </>
                ) : (
                  <>
                    <small>
                      {l.tipo === "cliente" ? "Cliente" : "IA"} · {l.quando}
                    </small>
                    <p>{l.tipo === "ia" ? semMarkdown(l.texto) : l.texto}</p>
                    {l.tipo === "ia" && <Artigo titulo={l.artigo || null} link={l.link || null} />}
                  </>
                )}
              </li>
            ))}
          </ol>
        ) : (
          <p className="metricas-vazio">Conversa não encontrada (pode ter passado dos 180 dias guardados).</p>
        )}
      </div>
    </div>
  );
};

const appDoSlug = (slug: string) => EmpreenderApps.find((a) => a.slug === slug || a.sluges === slug);

const nomeDoApp = (slug: string) => appDoSlug(slug)?.name || slug;

// Os dois slugs do app (português e espanhol): o filtro pega as conversas nos dois idiomas.
const slugsDoApp = (slug: string) => {
  const app = appDoSlug(slug);
  return app ? Array.from(new Set([app.slug, app.sluges])) : [slug];
};

type LinhaApp = Dados["porApp"][number];

// A tabela de apps vem do banco separada por slug; aqui o português e o espanhol viram uma linha só.
const juntarPorApp = (linhas: LinhaApp[]) => {
  const porApp = new Map<string, LinhaApp>();
  for (const l of linhas) {
    const chave = appDoSlug(l.app)?.slug || l.app;
    const atual = porApp.get(chave);
    porApp.set(
      chave,
      atual
        ? {
            app: chave,
            perguntas: atual.perguntas + l.perguntas,
            sem_artigo: atual.sem_artigo + l.sem_artigo,
            resolveu: atual.resolveu + l.resolveu,
            nao_resolveu: atual.nao_resolveu + l.nao_resolveu,
            atendente: atual.atendente + l.atendente,
          }
        : { ...l, app: chave }
    );
  }
  return Array.from(porApp.values()).sort((a, b) => b.perguntas - a.perguntas);
};

const hojeMenos = (dias: number) => {
  const d = new Date(Date.now() - dias * 86_400_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const pct = (parte: number, todo: number) => (todo ? Math.round((parte / todo) * 100) : null);
const fmtPct = (valor: number | null) => (valor === null ? "—" : `${valor}%`);
const fmtNum = (valor: number) => valor.toLocaleString("pt-BR");

// Taxa de resolução = "sim" ÷ (sim + não) no "Isso resolveu?".
const taxa = (resolveu: number, naoResolveu: number) => pct(resolveu, resolveu + naoResolveu);

const BarraTaxa: React.FC<{ valor: number | null }> = ({ valor }) => (
  <div className="metricas-taxa">
    <div className="metricas-taxa-trilho">
      {valor !== null && <div className="metricas-taxa-cheia" style={{ width: `${valor}%` }} />}
    </div>
    <span>{fmtPct(valor)}</span>
  </div>
);

const GraficoDiario: React.FC<{ dias: Dados["porDia"] }> = ({ dias }) => {
  const maximo = Math.max(1, ...dias.map((d) => d.perguntas));
  return (
    <div className="metricas-grafico" role="img" aria-label="Perguntas por dia">
      {dias.map((d) => {
        const t = taxa(d.resolveu, d.nao_resolveu);
        const dia = d.dia.slice(8, 10) + "/" + d.dia.slice(5, 7);
        return (
          <div
            key={d.dia}
            className="metricas-grafico-coluna"
            title={`${dia}: ${d.perguntas} perguntas · ${d.resolveu} resolveu · ${d.nao_resolveu} não resolveu`}
          >
            <span className="metricas-grafico-valor">{d.perguntas || ""}</span>
            <div className="metricas-grafico-barra" style={{ height: `${(d.perguntas / maximo) * 100}%` }}>
              {t !== null && <div className="metricas-grafico-resolvido" style={{ height: `${t}%` }} />}
            </div>
            <span className="metricas-grafico-dia">{dia}</span>
          </div>
        );
      })}
    </div>
  );
};

interface Usuario {
  nome: string;
  email: string;
  admin: boolean;
  tem_senha?: boolean;
}

// Menu da conta no topo: a inicial e o nome (no celular, só a inicial); abre com o e-mail, "Trocar
// senha" (só para quem tem senha; quem entrou só pelo Slack não tem) e "Sair".
const MenuConta: React.FC<{ usuario: Usuario; aoTrocarSenha: () => void; aoSair: () => void }> = ({ usuario, aoTrocarSenha, aoSair }) => {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => !caixa.current?.contains(e.target as Node) && setAberto(false);
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setAberto(false);
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", esc);
    };
  }, [aberto]);
  const inicial = (usuario.nome.trim()[0] || usuario.email[0] || "?").toUpperCase();
  return (
    <div className="metricas-conta" ref={caixa}>
      <button
        type="button"
        className="metricas-conta-botao"
        aria-haspopup="menu"
        aria-expanded={aberto}
        onClick={() => setAberto((a) => !a)}
      >
        <span className="metricas-conta-inicial" aria-hidden="true">{inicial}</span>
        <span className="metricas-conta-nome">{usuario.nome}</span>
        <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M9.75 4.125L6 7.875L2.25 4.125" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {aberto && (
        <div className="metricas-conta-menu" role="menu">
          <div className="metricas-conta-quem">
            <strong>{usuario.nome}</strong>
            <small>{usuario.email}</small>
            {usuario.admin && <span className="metricas-selo">admin</span>}
          </div>
          {usuario.tem_senha !== false && (
            <button type="button" role="menuitem" onClick={() => { setAberto(false); aoTrocarSenha(); }}>
              Trocar senha
            </button>
          )}
          <button type="button" role="menuitem" className="metricas-conta-sair" onClick={aoSair}>
            Sair
          </button>
        </div>
      )}
    </div>
  );
};

// Chamada às rotas do painel: o cookie da sessão vai sozinho (mesma origem).
const chamar = (rota: string, corpo: unknown) =>
  fetch(rota, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(corpo),
  });

// Mensagens genéricas de propósito: esta tela abre antes do login, então não diz o que falta
// configurar nem se o e-mail existe (o detalhe fica no log do servidor).
const MENSAGENS: Record<string, string> = {
  credenciais: "E-mail ou senha incorretos.",
  desativado: "Seu acesso ao painel está desativado. Fale com um admin.",
  limite: "Muitas tentativas. Aguarde alguns minutos e tente de novo.",
  indisponivel: "O painel está indisponível no momento.",
};

// Link de um aviso do Slack (#conversa=123, #app=slug) aberto sem sessão: a ida e a volta do Slack
// perdem o "#", então ele fica guardado na aba e é usado ao voltar logado.
const CHAVE_LINK = "metricas:link-do-aviso";
const guardarLinkParaDepoisDoSlack = () => {
  try {
    if (window.location.hash) sessionStorage.setItem(CHAVE_LINK, window.location.hash);
  } catch {
    /* sem armazenamento: entra no painel sem abrir a conversa */
  }
};
const linkDoAviso = () => {
  if (window.location.hash) return window.location.hash;
  try {
    const guardado = sessionStorage.getItem(CHAVE_LINK) || "";
    sessionStorage.removeItem(CHAVE_LINK);
    return guardado;
  } catch {
    return "";
  }
};

// Volta do "Logar com o Slack" sem entrar (?slack=motivo, ver api/slack-login.ts).
const MENSAGENS_SLACK: Record<string, string> = {
  cancelado: "O login pelo Slack foi cancelado.",
  expirou: "O login pelo Slack expirou. Tente de novo.",
  workspace: "Entre com a sua conta do Slack da Empreender.",
  conta: "Sua conta do Slack não tem acesso ao painel (convidados não entram).",
  desativado: "Seu acesso ao painel está desativado. Fale com um admin.",
  limite: "Muitas tentativas. Aguarde alguns minutos e tente de novo.",
  indisponivel: "O login pelo Slack está indisponível no momento.",
  erro: "Não foi possível entrar pelo Slack. Tente de novo em instantes.",
};

// Lido uma vez só (o React pode chamar o inicializador duas vezes) e tirado do endereço.
let mensagemDoSlack: string | null | undefined;
const motivoDoSlack = () => {
  if (mensagemDoSlack !== undefined) return mensagemDoSlack;
  const motivo = new URLSearchParams(window.location.search).get("slack");
  mensagemDoSlack = motivo ? MENSAGENS_SLACK[motivo] || MENSAGENS_SLACK.erro : null;
  if (motivo) window.history.replaceState(null, "", window.location.pathname + window.location.hash);
  return mensagemDoSlack;
};

const Entrar: React.FC<{ aoEntrar: (usuario: Usuario) => void }> = ({ aoEntrar }) => {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(motivoDoSlack);
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    setEnviando(true);
    setErro(null);
    try {
      const resposta = await chamar("/api/admin", { acao: "entrar", email, senha });
      const json = await resposta.json().catch(() => ({}));
      if (!resposta.ok) {
        setErro(MENSAGENS[json?.erro] || "Não foi possível entrar. Tente de novo em instantes.");
        return;
      }
      setSenha("");
      aoEntrar(json.usuario as Usuario);
    } catch {
      setErro("Sem conexão com o servidor.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="metricas-login">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (email && senha) enviar();
        }}
      >
        <h1>Métricas da central</h1>
        <p>Painel interno da Empreender. Entre com a sua conta do Slack.</p>
        <a className="metricas-slack" href="/api/slack-login" onClick={guardarLinkParaDepoisDoSlack}>
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#E01E5A" d="M5.04 15.17a2.53 2.53 0 1 1-2.52-2.53h2.52v2.53Zm1.27 0a2.53 2.53 0 0 1 5.05 0v6.31a2.53 2.53 0 1 1-5.05 0v-6.31Z" />
            <path fill="#36C5F0" d="M8.83 5.04a2.53 2.53 0 1 1 2.53-2.52v2.52H8.83Zm0 1.27a2.53 2.53 0 0 1 0 5.05H2.52a2.53 2.53 0 0 1 0-5.05h6.31Z" />
            <path fill="#2EB67D" d="M18.96 8.83a2.53 2.53 0 1 1 2.52 2.53h-2.52V8.83Zm-1.27 0a2.53 2.53 0 0 1-5.05 0V2.52a2.53 2.53 0 0 1 5.05 0v6.31Z" />
            <path fill="#ECB22E" d="M15.17 18.96a2.53 2.53 0 1 1-2.53 2.52v-2.52h2.53Zm0-1.27a2.53 2.53 0 0 1 0-5.05h6.31a2.53 2.53 0 0 1 0 5.05h-6.31Z" />
          </svg>
          Logar com o Slack
        </a>
        <p className="metricas-ou">ou com e-mail e senha</p>
        <input
          type="email"
          autoComplete="username"
          autoFocus
          placeholder="E-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          type="password"
          autoComplete="current-password"
          placeholder="Senha"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
        />
        {erro && <p className="metricas-erro">{erro}</p>}
        <button type="submit" disabled={enviando || !email || !senha}>
          {enviando ? "Entrando…" : "Entrar"}
        </button>
      </form>
    </div>
  );
};

const AdminMetricas: React.FC = () => {
  // undefined = ainda conferindo se já existe sessão; null = ninguém logado.
  const [usuario, setUsuario] = useState<Usuario | null | undefined>(undefined);
  const [inicio, setInicio] = useState(hojeMenos(29));
  const [fim, setFim] = useState(hojeMenos(0));
  const [app, setApp] = useState("");
  const [canal, setCanal] = useState("");
  const [dados, setDados] = useState<Dados | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [aberta, setAberta] = useState<number | null>(null);
  const [abertaTop, setAbertaTop] = useState<number | null>(null);
  // undefined = janela fechada; null = carregando.
  const [conversa, setConversa] = useState<LinhaConversa[] | null | undefined>(undefined);
  const fecharConversa = useCallback(() => setConversa(undefined), []);
  const [aba, setAba] = useState<"metricas" | "lps" | "base" | "usuarios" | "responsaveis" | "integracoes">("metricas");
  const [slack, setSlack] = useState<EstadoSlack | null>(null);

  const carregarSlack = useCallback(() => {
    chamarSlack({ acao: "estado" })
      .then(async (r) => (r.ok ? setSlack((await r.json()) as EstadoSlack) : setSlack({ configurado: false, responsaveis: [] })))
      .catch(() => setSlack({ configurado: false, responsaveis: [] }));
  }, []);
  const [trocandoSenha, setTrocandoSenha] = useState(false);
  // A aba Landing pages avisa quando a sessão venceu (volta para o login).
  const sessaoVenceu = useCallback(() => {
    setUsuario(null);
    setDados(null);
  }, []);

  // No celular as tabelas viram cartões (metricas.css): cada célula leva o nome da coluna em
  // data-rotulo, copiado do cabeçalho, para continuar dizendo o que é cada número.
  const raiz = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!raiz.current) return;
    const rotular = () =>
      raiz.current?.querySelectorAll("table.metricas-tabela").forEach((tabela) => {
        const nomes = Array.from(tabela.querySelectorAll("thead th"), (th) => th.textContent?.trim() || "");
        if (!nomes.length) return;
        tabela.querySelectorAll("tbody tr").forEach((tr) =>
          Array.from(tr.children).forEach((td, i) => {
            if (nomes[i] && td.getAttribute("data-rotulo") !== nomes[i]) td.setAttribute("data-rotulo", nomes[i]);
          })
        );
      });
    rotular();
    const observador = new MutationObserver(rotular);
    observador.observe(raiz.current, { childList: true, subtree: true });
    return () => observador.disconnect();
  }, [usuario]);

  // Ao abrir, confere se a sessão (cookie) ainda vale.
  useEffect(() => {
    chamar("/api/admin", { acao: "sessao" })
      .then(async (r) => (r.ok ? setUsuario((await r.json()).usuario as Usuario) : setUsuario(null)))
      .catch(() => setUsuario(null));
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const resposta = await chamar("/api/metricas", { inicio, fim, app: app ? slugsDoApp(app) : undefined, canal: canal || undefined });
      const json = await resposta.json().catch(() => ({}));
      if (resposta.status === 401) {
        // Sessão vencida (12 h) ou encerrada: volta para o login.
        setUsuario(null);
        setDados(null);
        return;
      }
      if (!resposta.ok) {
        setErro(MENSAGENS[json?.erro] || "Não foi possível carregar as métricas. Tente de novo em instantes.");
        return;
      }
      setDados(json as Dados);
    } catch {
      setErro("Sem conexão com o servidor das métricas.");
    } finally {
      setCarregando(false);
    }
  }, [inicio, fim, app, canal]);

  // Ao entrar: estado do Slack e, se veio pelo link de um aviso, abre a conversa (#conversa=123)
  // ou filtra o app do alerta de pico (#app=slug).
  useEffect(() => {
    if (!usuario) return;
    carregarSlack();
    const link = linkDoAviso();
    const doLink = /^#conversa=(\d{1,15})$/.exec(link)?.[1];
    const appDoLink = /^#app=([a-z0-9-]{2,60})$/.exec(link)?.[1];
    if (doLink || appDoLink) window.history.replaceState(null, "", window.location.pathname);
    if (doLink) abrirConversa(Number(doLink));
    if (appDoLink && appDoSlug(appDoLink)) {
      setApp(appDoSlug(appDoLink)!.slug);
      setInicio(hojeMenos(0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario]);

  // Carrega ao entrar e quando os filtros mudam.
  useEffect(() => {
    if (usuario) carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario, inicio, fim, app, canal]);

  const apps = useMemo(() => appsDaCentral("pt"), []);

  if (usuario === undefined) return <div className="metricas-login" />;
  if (!usuario) return <Entrar aoEntrar={setUsuario} />;

  const t = dados?.totais;
  const avaliadas = t ? t.resolveu + t.nao_resolveu : 0;
  const atendentes = dados?.motivos.filter((m) => m.tipo === "atendente") || [];
  const motivos = dados?.motivos.filter((m) => m.tipo === "motivo") || [];

  const periodoRapido = (dias: number) => {
    setInicio(hojeMenos(dias - 1));
    setFim(hojeMenos(0));
  };

  const abrirConversa = async (id: number) => {
    setConversa(null);
    try {
      const resposta = await chamar("/api/metricas", { acao: "conversa", id });
      if (resposta.status === 401) {
        setConversa(undefined);
        setUsuario(null);
        return;
      }
      const json = await resposta.json().catch(() => ({}));
      setConversa(Array.isArray(json?.conversa) ? json.conversa : []);
    } catch {
      setConversa([]);
    }
  };

  const sairDoPainel = async () => {
    try {
      await chamar("/api/admin", { acao: "sair" });
    } catch {}
    setUsuario(null);
    setDados(null);
  };

  return (
    <div className="metricas" ref={raiz}>
      {conversa !== undefined && <JanelaConversa linhas={conversa} aoFechar={fecharConversa} />}
      <header className="metricas-topo">
        <div>
          <h1>Métricas da central</h1>
          <p>Onde o suporte está atuando, o que resolve e o que não resolve.</p>
        </div>
        <MenuConta usuario={usuario} aoTrocarSenha={() => setTrocandoSenha(true)} aoSair={sairDoPainel} />
      </header>

      {trocandoSenha && <TrocarSenha aoFechar={() => setTrocandoSenha(false)} />}

      {/* Métricas e Landing pages para todos; Usuários, Responsáveis e Integrações só para admins (o
          servidor também recusa quem não é). */}
      <nav className="metricas-abas">
        <button className={aba === "metricas" ? "ativo" : ""} onClick={() => setAba("metricas")}>
          Métricas
        </button>
        <button className={aba === "lps" ? "ativo" : ""} onClick={() => setAba("lps")}>
          Landing pages
        </button>
        {usuario.admin && (
          <>
            <button className={aba === "usuarios" ? "ativo" : ""} onClick={() => setAba("usuarios")}>
              Usuários
            </button>
            <button className={aba === "responsaveis" ? "ativo" : ""} onClick={() => setAba("responsaveis")}>
              Responsáveis
            </button>
            <button className={aba === "integracoes" ? "ativo" : ""} onClick={() => setAba("integracoes")}>
              Integrações
            </button>
            <button className={aba === "base" ? "ativo" : ""} onClick={() => setAba("base")}>
              Base de conhecimento
            </button>
          </>
        )}
      </nav>

      {aba === "lps" ? (
        <AdminLps aoSessaoVencer={sessaoVenceu} />
      ) : aba === "base" && usuario.admin ? (
        <AdminBase />
      ) : aba === "integracoes" && usuario.admin ? (
        <AdminIntegracoes />
      ) : aba === "responsaveis" && usuario.admin ? (
        <AdminResponsaveis slack={slack} aoMudar={carregarSlack} />
      ) : aba === "usuarios" && usuario.admin ? (
        <AdminUsuarios />
      ) : (
        <>

      <section className="metricas-filtros">
        <div className="metricas-rapidos">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => periodoRapido(d)} className={inicio === hojeMenos(d - 1) && fim === hojeMenos(0) ? "ativo" : ""}>
              {d} dias
            </button>
          ))}
        </div>
        <label>
          De <input type="date" value={inicio} max={fim} onChange={(e) => setInicio(e.target.value)} />
        </label>
        <label>
          Até <input type="date" value={fim} min={inicio} onChange={(e) => setFim(e.target.value)} />
        </label>
        <label>
          App
          <select value={app} onChange={(e) => setApp(e.target.value)}>
            <option value="">Todos</option>
            {apps.map((a) => (
              <option key={a.slugamigavel} value={a.slug}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Onde
          <select value={canal} onChange={(e) => setCanal(e.target.value)}>
            <option value="">Site e painéis</option>
            <option value="site">Site (suporte.com.br)</option>
            <option value="embed">"Ajuda" dos painéis</option>
          </select>
        </label>
        {carregando && <span className="metricas-carregando">Atualizando…</span>}
      </section>

      {erro && <p className="metricas-erro">{erro}</p>}

      {dados && t && (
        <>
          <section className="metricas-cartoes">
            <div className="metricas-cartao destaque">
              <span>Taxa de resolução</span>
              <strong>{fmtPct(taxa(t.resolveu, t.nao_resolveu))}</strong>
              <small>
                {fmtNum(t.resolveu)} resolveu · {fmtNum(t.nao_resolveu)} não resolveu
              </small>
            </div>
            <div className="metricas-cartao">
              <span>Perguntas</span>
              <strong>{fmtNum(t.perguntas)}</strong>
              <small>{fmtNum(t.conversas)} conversas</small>
            </div>
            <div className="metricas-cartao">
              <span>Avaliadas</span>
              <strong>{fmtPct(pct(avaliadas, t.perguntas))}</strong>
              <small>responderam o "Isso resolveu?"</small>
            </div>
            <div className="metricas-cartao">
              <span>Foram para atendente</span>
              <strong>{fmtPct(pct(t.atendente, t.conversas))}</strong>
              <small>{fmtNum(t.atendente)} conversas</small>
            </div>
            <div className="metricas-cartao">
              <span>Sem artigo</span>
              <strong>{fmtPct(pct(t.sem_artigo, t.perguntas))}</strong>
              <small>{fmtNum(t.sem_artigo)} perguntas sem resposta na central</small>
            </div>
            <div className="metricas-cartao">
              <span>Tempo médio de resposta</span>
              <strong>{t.tempo_medio_ms ? `${(t.tempo_medio_ms / 1000).toFixed(1)} s` : "—"}</strong>
              <small>{t.plano_b ? `${fmtNum(t.plano_b)} pelo plano B (SAK)` : "todas pela IA"}</small>
            </div>
          </section>

          <section className="metricas-bloco">
            <h2>Perguntas por dia</h2>
            <p className="metricas-legenda">
              Altura = perguntas do dia; parte escura = taxa de resolução do dia (entre as avaliadas).
            </p>
            {t.perguntas ? <GraficoDiario dias={dados.porDia} /> : <p className="metricas-vazio">Nenhuma pergunta no período.</p>}
          </section>

          <div className="metricas-duas">
            <section className="metricas-bloco">
              <h2>Apps</h2>
              <table className="metricas-tabela metricas-tabela-apps">
                <thead>
                  <tr>
                    <th>App</th>
                    <th>Perguntas</th>
                    <th>Resolução</th>
                    <th>Sem artigo</th>
                    <th>Atendente</th>
                  </tr>
                </thead>
                <tbody>
                  {juntarPorApp(dados.porApp).map((a) => (
                    <tr key={a.app}>
                      <td>
                        <button className="metricas-link" onClick={() => setApp(a.app)}>
                          {nomeDoApp(a.app)}
                        </button>
                      </td>
                      <td>{fmtNum(a.perguntas)}</td>
                      <td>
                        <BarraTaxa valor={taxa(a.resolveu, a.nao_resolveu)} />
                      </td>
                      <td>{fmtPct(pct(a.sem_artigo, a.perguntas))}</td>
                      <td>{fmtNum(a.atendente)}</td>
                    </tr>
                  ))}
                  {!dados.porApp.length && (
                    <tr>
                      <td colSpan={5} className="metricas-vazio">
                        Sem dados no período.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>

            <section className="metricas-bloco">
              <h2>Perguntas mais feitas</h2>
              <ol className="metricas-lista">
                {dados.topPerguntas.map((p) => (
                  <li key={p.id}>
                    <span className="metricas-contagem">{p.vezes}×</span>
                    <div className="metricas-item">
                      <button className="metricas-item-topo" onClick={() => setAbertaTop(abertaTop === p.id ? null : p.id)}>
                        <p>{p.exemplo}</p>
                        <small>
                          {nomeDoApp(p.app)}
                          {p.sem_artigo > 0 && <span className="metricas-selo">sem artigo</span>}
                          {(p.usou_material || 0) > 0 && <span className="metricas-selo">usou material</span>}
                        </small>
                      </button>
                      {abertaTop === p.id && (
                        <div className="metricas-resposta metricas-resposta-lista">
                          <p>{p.resposta ? semMarkdown(p.resposta) : "(resposta não registrada)"}</p>
                          <Artigo titulo={p.artigo_titulo} link={p.artigo_link} />
                          <small className="metricas-avaliacao">
                            {p.resolveu + p.nao_resolveu
                              ? `${fmtNum(p.resolveu)} resolveu · ${fmtNum(p.nao_resolveu)} não resolveu`
                              : "Ninguém avaliou essa resposta ainda"}
                          </small>
                          <AvisarSlack id={p.id} slugs={slugsDoApp(p.app)} appNome={nomeDoApp(p.app)} aviso={p.aviso} slack={slack} />
                        </div>
                      )}
                    </div>
                  </li>
                ))}
                {!dados.topPerguntas.length && <li className="metricas-vazio">Sem perguntas no período.</li>}
              </ol>
            </section>
          </div>

          <section className="metricas-bloco">
            <h2>Respostas marcadas como "não resolveu"</h2>
            <p className="metricas-legenda">As mais recentes primeiro. Clique para ver a resposta da IA e o que o cliente fez depois.</p>
            <div className="metricas-nao-resolvidas">
              {dados.naoResolvidas.map((r) => (
                <article key={`${r.id}-${r.quando}`} className={aberta === r.id ? "aberta" : ""}>
                  <button className="metricas-nao-resolvida-topo" onClick={() => setAberta(aberta === r.id ? null : r.id)}>
                    <span className="metricas-quando">{r.quando}</span>
                    <span className="metricas-pergunta">{r.pergunta}</span>
                    <span className="metricas-tags">
                      <span className="metricas-selo">{nomeDoApp(r.app)}</span>
                      {r.motivo && <span className="metricas-selo">{NOME_MOTIVO[r.motivo] || r.motivo}</span>}
                      {r.usou_material && <span className="metricas-selo">usou material</span>}
                      {r.foi_atendente && <span className="metricas-selo atendente">foi para atendente</span>}
                      {r.canal === "embed" && <span className="metricas-selo">painel</span>}
                      {r.aviso && <span className="metricas-selo slack">avisado no Slack</span>}
                    </span>
                  </button>
                  {aberta === r.id && (
                    <div className="metricas-resposta">
                      <p>{r.resposta ? semMarkdown(r.resposta) : "(resposta não registrada)"}</p>
                      <Artigo titulo={r.artigo_titulo} link={r.artigo_link} />
                      {r.depois.length > 0 && (
                        <div className="metricas-extra">
                          <span>O cliente escreveu depois</span>
                          {r.depois.map((texto, i) => (
                            <p key={i} title={texto}>
                              {texto}
                            </p>
                          ))}
                        </div>
                      )}
                      {r.resumo_atendente && (
                        <div className="metricas-extra">
                          <span>Enviado ao atendente</span>
                          <p title={r.resumo_atendente}>{r.resumo_atendente.replace(/\s*\n\s*/g, " · ")}</p>
                        </div>
                      )}
                      <button className="metricas-link metricas-ver-conversa" onClick={() => abrirConversa(r.id)}>
                        Ver conversa completa
                      </button>
                      <AvisarSlack id={r.id} slugs={slugsDoApp(r.app)} appNome={nomeDoApp(r.app)} aviso={r.aviso} slack={slack} />
                    </div>
                  )}
                </article>
              ))}
              {!dados.naoResolvidas.length && <p className="metricas-vazio">Nenhuma resposta marcada como "não resolveu" no período.</p>}
            </div>
          </section>

          <div className="metricas-duas">
            <section className="metricas-bloco">
              <h2>Lacunas: perguntas sem artigo</h2>
              <p className="metricas-legenda">O que a central não sabe responder. Cada linha é um artigo a escrever.</p>
              <ol className="metricas-lista">
                {dados.lacunas.map((l, i) => (
                  <li key={i}>
                    <span className="metricas-contagem">{l.vezes}×</span>
                    <div>
                      <p>{l.exemplo}</p>
                      <small>
                        {nomeDoApp(l.app)} · última em {l.ultima}
                      </small>
                      <AvisarSlack id={l.id} slugs={slugsDoApp(l.app)} appNome={nomeDoApp(l.app)} aviso={l.aviso} slack={slack} compacto />
                    </div>
                  </li>
                ))}
                {!dados.lacunas.length && <li className="metricas-vazio">Nenhuma pergunta sem artigo no período.</li>}
              </ol>
            </section>

            <section className="metricas-bloco">
              <h2>Artigos mais usados</h2>
              <table className="metricas-tabela">
                <thead>
                  <tr>
                    <th>Artigo</th>
                    <th>Usos</th>
                    <th>Resolução</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.artigos.map((a) => (
                    <tr key={a.titulo}>
                      <td>
                        {linkSeguro(a.link) ? (
                          <a href={linkSeguro(a.link)!} target="_blank" rel="noopener noreferrer">
                            {a.titulo}
                          </a>
                        ) : (
                          a.titulo
                        )}
                        <small>{nomeDoApp(a.app)}</small>
                      </td>
                      <td>{fmtNum(a.vezes)}</td>
                      <td>
                        <BarraTaxa valor={taxa(a.resolveu, a.nao_resolveu)} />
                      </td>
                    </tr>
                  ))}
                  {!dados.artigos.length && (
                    <tr>
                      <td colSpan={3} className="metricas-vazio">
                        Sem artigos usados no período.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>
          </div>

          <div className="metricas-tres">
            <section className="metricas-bloco">
              <h2>O que faltou</h2>
              <ul className="metricas-contagens">
                {motivos.map((m) => (
                  <li key={m.detalhe}>
                    <span>{NOME_MOTIVO[m.detalhe] || m.detalhe}</span>
                    <strong>{fmtNum(m.vezes)}</strong>
                  </li>
                ))}
                {!motivos.length && <li className="metricas-vazio">Sem registros.</li>}
              </ul>
            </section>
            <section className="metricas-bloco">
              <h2>Por onde chamaram o atendente</h2>
              <ul className="metricas-contagens">
                {atendentes.map((m) => (
                  <li key={m.detalhe}>
                    <span>{NOME_ORIGEM_ATENDENTE[m.detalhe] || m.detalhe || "Não identificado"}</span>
                    <strong>{fmtNum(m.vezes)}</strong>
                  </li>
                ))}
                {!atendentes.length && <li className="metricas-vazio">Sem registros.</li>}
              </ul>
            </section>
            <section className="metricas-bloco">
              <h2>De onde vieram as perguntas</h2>
              <ul className="metricas-contagens">
                {dados.porOrigem.map((o) => (
                  <li key={`${o.canal}-${o.painel}`}>
                    <span>{o.canal === "site" ? "Site suporte.com.br" : o.painel ? o.painel.replace(/^https?:\/\//, "") : "Painel (domínio oculto)"}</span>
                    <strong>{fmtNum(o.perguntas)}</strong>
                  </li>
                ))}
                {!dados.porOrigem.length && <li className="metricas-vazio">Sem registros.</li>}
              </ul>
            </section>
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
};

export default AdminMetricas;
